import {useEffect, useRef} from "react";
import UpdateExtensionForm from "./UpdateExtensionForm";
import {FormattedFormRow} from "@dvrt/graphql-widgets";
import {ContextFilter, useAuth} from "@/context/AuthProvider";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {updateExtensionInputFieldDescriptor} from "./fieldDescriptors";
import {fieldWidgetDescriptors, RowDescriptor} from "@/components/extension/settings/fieldWidgets";
import {
    CustomWidgetRenderers,
    FieldName,
    FieldWidgetRenderer,
    UpdateExtensionDataFieldDesc,
} from "@/components/extension/settings/types";
import {headerHeight} from "@/styles";
import {FirstStageQueryResult, SecondStageQueryResult} from "./queries.generated";
import {DomainOutgoingCallBarrings} from "@/hooks";

type Group = SecondStageQueryResult["data"]["groups"]["items"][number];

type FieldProps = {
    field: FieldName;
    form: UpdateExtensionForm;
    scrollTo: boolean;
    enabled: boolean;
    isResidential: boolean;
    extension: FirstStageQueryResult["data"]["users"]["items"][number];
    locations: SecondStageQueryResult["data"]["locations"]["items"];
    overrideCallerNumbers: SecondStageQueryResult["data"]["overrideNumbers"];
    secretaryNumbers: SecondStageQueryResult["data"]["secretaryNumbers"];
    groups: Group[];
    domainCallBarrings: DomainOutgoingCallBarrings;
    extensionContextFilter: ContextFilter;
    customFieldWidgetRenderer?: FieldWidgetRenderer<any>;
};

const Field = ({
    field,
    form,
    scrollTo,
    enabled,
    isResidential,
    extension,
    locations,
    overrideCallerNumbers,
    secretaryNumbers,
    groups,
    domainCallBarrings,
    extensionContextFilter,
    customFieldWidgetRenderer,
}: FieldProps) => {
    const auth = useAuth();
    const {isEnabledFeature} = useStructureConfig();
    const inputRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollTo == true) inputRef.current.scrollIntoView({behavior: "smooth"});
    }, [scrollTo]);

    const {render, rowProps, label}: RowDescriptor<any> = fieldWidgetDescriptors[field];
    const fieldDesc: UpdateExtensionDataFieldDesc = updateExtensionInputFieldDescriptor[field];
    if (fieldDesc.businessOnly && isResidential) return null;
    if (fieldDesc.requiredFeature && !isEnabledFeature(fieldDesc.requiredFeature, form.values.previewFeaturesEnabled))
        return null;

    const renderer = customFieldWidgetRenderer ?? render;

    const widget = renderer({
        auth,
        form,
        field,
        disabled: !auth.hasPermission(fieldDesc.permission, extensionContextFilter) || !enabled,
        externalDisabled: !enabled,
        data: {
            extension,
            locations,
            overrideCallerNumbers,
            secretaryNumbers,
            groups,
            domainCallBarrings,
            getGroupById: (id: number) => groups.find((g) => g.id === id),
        },
        value: form.values[field],
        extensionContextFilter: extensionContextFilter,
        updateValue: (value) => form.updateValues({[field]: value}),
        errors: form.errorHelper.fieldErrors(field),
    });

    if (widget === null) return null;

    return (
        <FormattedFormRow
            label={label}
            {...rowProps}
            inputRef={inputRef}
            inputStyle={{scrollMargin: headerHeight + 10}}
        >
            {widget}
        </FormattedFormRow>
    );
};

export type UpdateExtensionFieldListProps = {
    fields: FieldName[];
    form: UpdateExtensionForm;
    enabled: boolean;
    isResidential: boolean;
    extension: FirstStageQueryResult["data"]["users"]["items"][number];
    locations: SecondStageQueryResult["data"]["locations"]["items"];
    overrideCallerNumbers: SecondStageQueryResult["data"]["overrideNumbers"];
    secretaryNumbers: SecondStageQueryResult["data"]["secretaryNumbers"];
    groups: Group[];
    domainCallBarrings: DomainOutgoingCallBarrings;
    extensionContextFilter: ContextFilter;
    customWidgetRenderers?: CustomWidgetRenderers;
};

const UpdateExtensionFieldList = ({
    fields,
    form,
    enabled,
    isResidential,
    extension,
    locations,
    overrideCallerNumbers,
    secretaryNumbers,
    groups,
    domainCallBarrings,
    extensionContextFilter,
    customWidgetRenderers = {},
}: UpdateExtensionFieldListProps) => {
    let firstFieldWithError: FieldName = null;

    for (const field of fields) {
        if (form.errorHelper.hasError(field)) {
            firstFieldWithError = field;
            break;
        }
    }

    return (
        <>
            {fields.map((field) => (
                <Field
                    key={field}
                    field={field}
                    form={form}
                    scrollTo={field === firstFieldWithError}
                    enabled={enabled}
                    isResidential={isResidential}
                    extension={extension}
                    locations={locations}
                    overrideCallerNumbers={overrideCallerNumbers}
                    secretaryNumbers={secretaryNumbers}
                    groups={groups}
                    domainCallBarrings={domainCallBarrings}
                    customFieldWidgetRenderer={customWidgetRenderers[field]}
                    extensionContextFilter={extensionContextFilter}
                />
            ))}
        </>
    );
};

export default UpdateExtensionFieldList;
